import { Component, ChangeDetectionStrategy, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TextFieldModule } from '@angular/cdk/text-field';
import { Field, StructureType, StructureTypeCreateDTO } from '../../../types/fields';
import { FieldPickerComponent } from './field-picker.component';

export interface StructureDialogData {
  mode: 'create' | 'edit';
  structure?: StructureType;
  allFields: Field[];
}

export interface StructureDialogResult {
  action: 'save' | 'cancel';
  data?: StructureTypeCreateDTO;
  structureId?: string;
}

const COLOR_PALETTE: { value: string; name: string }[] = [
  { value: '#3b82f6', name: 'Blue' },
  { value: '#22c55e', name: 'Green' },
  { value: '#a855f7', name: 'Purple' },
  { value: '#f97316', name: 'Orange' },
  { value: '#ef4444', name: 'Red' },
  { value: '#14b8a6', name: 'Teal' },
  { value: '#ec4899', name: 'Pink' },
  { value: '#64748b', name: 'Slate' },
  { value: '#eab308', name: 'Yellow' },
  { value: '#06b6d4', name: 'Cyan' },
  { value: '#6366f1', name: 'Indigo' },
];

@Component({
  selector: 'app-structure-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TextFieldModule,
    FieldPickerComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col h-full overflow-hidden bg-white dark:bg-zinc-900">

      <!-- ── Header ────────────────────────────────────── -->
      <div class="flex items-center gap-3 px-6 py-4 border-b border-zinc-200 dark:border-zinc-700 shrink-0">
        <!-- Live preview: color accent + name -->
        <div
          class="h-8 w-2 rounded-full shrink-0 transition-colors"
          [style.backgroundColor]="selectedColor()"
        ></div>
        <div class="flex-1 min-w-0">
          <h2 data-testid="structure-dialog-heading" class="text-lg font-semibold text-zinc-900 dark:text-white leading-tight truncate">
            {{ previewName() }}
          </h2>
          <p class="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
            {{ data.mode === 'edit' ? 'Edit structure' : 'New structure' }}
            &middot; {{ selectedFieldIds().length }} field{{ selectedFieldIds().length !== 1 ? 's' : '' }}
          </p>
        </div>
        <button
          type="button"
          aria-label="Close"
          class="rounded-full p-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors cursor-pointer shrink-0"
          (click)="onCancel()"
        >
          <mat-icon class="!text-xl text-zinc-500 dark:text-zinc-400">close</mat-icon>
        </button>
      </div>

      <!-- Edit-mode warning -->
      @if (data.mode === 'edit') {
        <div class="flex items-start gap-2 px-5 py-2.5 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 shrink-0">
          <mat-icon class="!text-sm !w-4 !h-4 !leading-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400">warning</mat-icon>
          <p class="text-xs text-amber-800 dark:text-amber-200">Editing fields on a structure already in use will affect all templates referencing it.</p>
        </div>
      }

      <!-- ── Form ──────────────────────────────────────── -->
      <form [formGroup]="form" (ngSubmit)="onSubmit()" class="flex flex-col flex-1 min-h-0">

        <!-- Scrollable body: on short viewports the body scrolls while the
             field picker keeps a usable minimum height instead of collapsing. -->
        <div class="flex flex-col flex-1 min-h-0 overflow-y-auto">

        <!-- Metadata section (scrollable top block) -->
        <div class="px-6 py-4 border-b border-zinc-100 dark:border-zinc-800 space-y-4 shrink-0">

          <!-- ── BASICS section header ── -->
          <h3 class="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-zinc-500 border-b border-zinc-100 dark:border-zinc-800 pb-1.5">Basics</h3>

          <!-- Name -->
          <div class="space-y-1.5">
            <label class="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
              Name <span class="text-red-500">*</span>
            </label>
            <input
              data-testid="structure-dialog-name-input"
              type="text"
              formControlName="name"
              placeholder="e.g., Invoice Details"
              class="flex h-9 w-full rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 dark:text-zinc-100 px-3 py-1.5 text-sm shadow-sm transition-colors placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              [class.border-red-300]="form.get('name')?.invalid && form.get('name')?.touched"
            >
            @if (form.get('name')?.hasError('required') && form.get('name')?.touched) {
              <p class="text-xs text-red-500 mt-1">Name is required</p>
            }
          </div>

          <!-- Color picker -->
          <div class="space-y-1.5">
            <label class="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Color</label>
            <div class="flex flex-wrap gap-1.5">
              @for (c of colorPalette; track c.value) {
                <button
                  type="button"
                  [attr.aria-label]="'Color: ' + c.name"
                  [matTooltip]="c.name"
                  (click)="selectColor(c.value)"
                  class="relative h-7 w-7 rounded-md transition-all cursor-pointer
                         flex items-center justify-center
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-zinc-500"
                  [class.ring-2]="selectedColor() === c.value"
                  [class.ring-offset-2]="selectedColor() === c.value"
                  [class.ring-zinc-800]="selectedColor() === c.value"
                  [class.dark:ring-white]="selectedColor() === c.value"
                  [style.backgroundColor]="c.value"
                >
                  @if (selectedColor() === c.value) {
                    <mat-icon class="!text-white !text-sm !w-4 !h-4 !leading-4">check</mat-icon>
                  }
                </button>
              }
            </div>
          </div>

          <!-- Description -->
          <div class="space-y-1.5">
            <label class="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Description</label>
            <textarea
              data-testid="structure-dialog-description-input"
              formControlName="description"
              cdkTextareaAutosize
              cdkAutosizeMinRows="2"
              cdkAutosizeMaxRows="4"
              placeholder="What kind of document or data does this structure represent?"
              class="w-full rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 dark:text-zinc-100 px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none min-h-[60px]"
            ></textarea>
          </div>
        </div>

        <!-- ── Fields section (picker fills remaining height; the min-height floor
             makes the body above scroll on short viewports instead of crushing the picker) ── -->
        <div class="flex flex-col flex-1 min-h-[400px] px-6 pt-4 pb-2 gap-2">
          <div class="flex items-center justify-between shrink-0">
            <h3 class="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">Fields</h3>
            @if (duplicateNames().size > 0) {
              <div
                data-testid="structure-dialog-duplicate-names-error"
                class="flex items-center gap-1 text-xs text-red-600 dark:text-red-400"
              >
                <mat-icon class="!text-sm !w-4 !h-4 !leading-4">error</mat-icon>
                Duplicate names: {{ duplicateNamesList() }}
              </div>
            }
          </div>

          <app-field-picker
            class="flex-1 min-h-0"
            [allFields]="data.allFields"
            [selectedIds]="selectedFieldIds()"
            (selectedIdsChange)="onFieldIdsChange($event)"
          />
        </div>

        </div>

        <!-- ── Sticky footer ─────────────────────────────── -->
        <div class="flex justify-end gap-3 px-6 py-4 border-t border-zinc-200 dark:border-zinc-700 shrink-0 bg-white dark:bg-zinc-900">
          <button
            data-testid="structure-dialog-cancel-btn"
            type="button"
            class="inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium bg-zinc-100 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-600 transition-colors cursor-pointer"
            (click)="onCancel()"
          >
            Cancel
          </button>
          <button
            data-testid="structure-dialog-submit-btn"
            type="submit"
            class="inline-flex items-center justify-center rounded-md px-4 py-1.5 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            [disabled]="form.invalid || submitting() || duplicateNames().size > 0"
          >
            @if (submitting()) {
              <mat-spinner diameter="16" class="mr-2"></mat-spinner>
            }
            {{ data.mode === 'edit' ? 'Save Structure' : 'Create Structure' }}
          </button>
        </div>
      </form>
    </div>
  `,
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: hidden;
    }

    mat-spinner ::ng-deep circle {
      stroke: white !important;
    }
  `]
})
export class StructureDialogComponent {
  private dialogRef = inject(MatDialogRef<StructureDialogComponent>);
  data = inject<StructureDialogData>(MAT_DIALOG_DATA);
  private fb = inject(FormBuilder);

  submitting = signal(false);
  selectedColor = signal(
    this.data.structure?.color ?? COLOR_PALETTE[Math.floor(Math.random() * COLOR_PALETTE.length)].value
  );
  selectedFieldIds = signal<string[]>(
    this.data.structure?.fieldIds ?? this.data.allFields.filter(f => f.isAlwaysVisible).map(f => f.id)
  );

  colorPalette = COLOR_PALETTE;

  form = this.fb.group({
    name: [this.data.structure?.name ?? '', [Validators.required]],
    description: [this.data.structure?.description ?? '']
  });

  previewName = computed(() => {
    const v = this.form.get('name')?.value?.trim();
    return v || (this.data.mode === 'edit' ? 'Untitled Structure' : 'New Structure');
  });

  duplicateNames = computed<Set<string>>(() => {
    const ids = new Set(this.selectedFieldIds());
    const counts = new Map<string, number>();
    for (const f of this.data.allFields) {
      if (ids.has(f.id)) {
        counts.set(f.name, (counts.get(f.name) ?? 0) + 1);
      }
    }
    const dupes = new Set<string>();
    for (const [name, count] of counts) {
      if (count > 1) dupes.add(name);
    }
    return dupes;
  });

  duplicateNamesList = computed(() => Array.from(this.duplicateNames()).join(', '));

  selectColor(color: string): void {
    this.selectedColor.set(color);
  }

  onFieldIdsChange(ids: string[]): void {
    this.selectedFieldIds.set(ids);
  }

  onSubmit(): void {
    if (this.form.invalid || this.duplicateNames().size > 0) return;

    const { name, description } = this.form.value;
    const dto: StructureTypeCreateDTO = {
      name: name!,
      description: description || undefined,
      color: this.selectedColor(),
      fieldIds: this.selectedFieldIds()
    };

    this.dialogRef.close({
      action: 'save',
      data: dto,
      structureId: this.data.mode === 'edit' ? this.data.structure?.id : undefined
    } as StructureDialogResult);
  }

  onCancel(): void {
    this.dialogRef.close({ action: 'cancel' } as StructureDialogResult);
  }
}
